import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Button } from '../../../../shared/components/Button.js';

export interface MemberOption {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly assigned: boolean;
  /** Set when the member currently belongs to a different parent. */
  readonly takenBy: string | null;
}

interface Props {
  readonly title: string;
  readonly emptyHint: string;
  readonly load: () => Promise<readonly MemberOption[]>;
  readonly save: (ids: readonly string[]) => Promise<readonly MemberOption[]>;
  readonly testId: string;
}

/**
 * Ticks the members that make up a zone, region or territory.
 *
 * The whole set is submitted at once, so unticking is a removal — that is what
 * makes the parent's definition exactly what is on screen rather than an
 * append-only list.
 */
export function MembershipPicker({ title, emptyHint, load, save, testId }: Props): ReactNode {
  const [rows, setRows] = useState<readonly MemberOption[] | null>(null);
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const apply = (list: readonly MemberOption[]) => {
    setRows(list);
    setPicked(new Set(list.filter((r) => r.assigned).map((r) => r.id)));
  };

  useEffect(() => {
    let alive = true;
    load()
      .then((r) => { if (alive) { apply(r); setError(null); } })
      .catch((e) => { if (alive) { setRows([]); setError((e as Error).message); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = rows ?? [];
    return q
      ? list.filter((r) => `${r.code} ${r.name}`.toLowerCase().includes(q))
      : list;
  }, [rows, filter]);

  const toggle = (id: string) => {
    setStatus(null);
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const onSave = async () => {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      apply(await save([...picked]));
      setStatus(`${picked.size} selected`);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  if (!rows) return <p>Loading…</p>;

  return (
    <section aria-label={testId} style={{ marginTop: 24 }}>
      <h2>{title}</h2>
      {error ? <p role="alert">{error}</p> : null}
      {status ? <p role="status">{status}</p> : null}

      {rows.length === 0 ? (
        <p data-testid={`${testId}-empty`}>{emptyHint}</p>
      ) : (
        <>
          <label htmlFor={`${testId}-filter`}>Search</label>
          <input
            id={`${testId}-filter`}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid={`${testId}-filter`}
          />
          <p data-testid={`${testId}-count`}>{picked.size} of {rows.length} selected</p>

          <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--color-border-default)', borderRadius: 6, padding: 8 }}>
            {visible.map((r) => (
              <label key={r.id} style={{ display: 'block', padding: '2px 0' }}>
                <input
                  type="checkbox"
                  checked={picked.has(r.id)}
                  onChange={() => toggle(r.id)}
                  data-testid={`${testId}-cb-${r.id}`}
                />
                {' '}{r.code} — {r.name}
                {r.takenBy && !picked.has(r.id) ? ' (already in another group)' : ''}
              </label>
            ))}
          </div>

          <Button type="button" variant="primary" onClick={() => void onSave()} disabled={busy} data-testid={`${testId}-save`}>
            Save selection
          </Button>
        </>
      )}
    </section>
  );
}
